import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { ApiEndpoints } from '../api/api-endpoints';
import {
  NotificationResponse,
  NotificationsListResponse,
  NotificationUnreadCountResponse,
} from '../models/notification.model';

export interface NotificationQuery {
  page: number;
  pageSize: number;
  unreadOnly?: boolean;
}

export interface NotificationPage {
  items: NotificationResponse[];
  totalCount: number;
}

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly http = inject(HttpClient);
  private readonly notificationsUrl = ApiEndpoints.notifications;

  getNotifications(query: NotificationQuery): Observable<NotificationPage> {
    const params = new HttpParams()
      .set('page', query.page.toString())
      .set('pageSize', query.pageSize.toString())
      .set('unreadOnly', String(query.unreadOnly ?? false));

    return this.http
      .get<NotificationsListResponse | NotificationResponse[]>(this.notificationsUrl, { params })
      .pipe(
        map((response) => {
          if (Array.isArray(response)) {
            return { items: response, totalCount: response.length };
          }

          const items = response.items ?? response.Items ?? response.data ?? response.Data ?? [];

          return {
            items,
            totalCount:
              response.totalCount ??
              response.TotalCount ??
              response.total ??
              response.Total ??
              items.length,
          };
        }),
      );
  }

  getUnreadCount(): Observable<number> {
    return this.http
      .get<NotificationUnreadCountResponse>(`${this.notificationsUrl}/unread-count`)
      .pipe(
        map((response) =>
          Number(response.count ?? response.unreadCount ?? response.unread_count ?? 0),
        ),
      );
  }

  markAsRead(notificationId: string): Observable<unknown> {
    return this.http.patch(`${this.notificationsUrl}/${notificationId}/mark-as-read`, {});
  }

  markAllAsRead(): Observable<unknown> {
    return this.http.patch(`${this.notificationsUrl}/mark-all-as-read`, {});
  }

  getNotificationId(notification: NotificationResponse): string {
    return notification.publicId ?? notification.public_id ?? notification.id ?? '';
  }
}
